// Activo Fijo → Toma Física → Etiquetas / QR (imprimibles: placa + QR).
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import QRCode from 'qrcode';
import { Loader2, Printer, QrCode } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { ActivoRow, afRpc, fieldCls } from './shared';

async function imprimir(items: { placa: string; nombre: string }[]) {
  const win = window.open('', '_blank');
  if (!win) { toast.error('Permite ventanas emergentes para imprimir'); return; }
  win.document.write('<p style="font-family:sans-serif;padding:2rem">Generando etiquetas…</p>');
  const celdas = await Promise.all(items.map(async (a) => {
    const qr = await QRCode.toDataURL(a.placa, { width: 140, margin: 1 });
    const esc = (s: string) => s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c] as string));
    return `<div class="lbl"><img src="${qr}"><div class="txt"><div class="placa">${esc(a.placa)}</div><div class="nom">${esc(a.nombre)}</div></div></div>`;
  }));
  win.document.open();
  win.document.write(`<!doctype html><html lang="es"><head><meta charset="utf-8"><title>Etiquetas de activos</title>
  <style>
    *{box-sizing:border-box} body{font-family:Arial,Helvetica,sans-serif;margin:10mm}
    .grid{display:grid;grid-template-columns:repeat(3,1fr);gap:6mm}
    .lbl{border:1px solid #999;border-radius:4px;padding:4mm;display:flex;gap:3mm;align-items:center;break-inside:avoid}
    .lbl img{width:22mm;height:22mm}
    .placa{font-size:14px;font-weight:bold;font-family:monospace}
    .nom{font-size:11px;color:#333;max-width:38mm;overflow:hidden}
    .btn{margin-bottom:8mm;padding:8px 14px;border:1px solid #111;background:#111;color:#fff;border-radius:6px;cursor:pointer}
    @media print{.noprint{display:none}}
  </style></head><body>
  <button class="btn noprint" onclick="window.print()">Imprimir</button>
  <div class="grid">${celdas.join('')}</div>
  <script>window.onload=function(){setTimeout(function(){window.print()},400)}</script>
  </body></html>`);
  win.document.close();
}

export function EtiquetasAF() {
  const [busq, setBusq] = useState('');
  const [sel, setSel] = useState<Record<string, boolean>>({});
  const { data: activos = [], isFetching } = useQuery<ActivoRow[]>({ queryKey: ['af-activos', 'activo', ''], queryFn: () => afRpc<ActivoRow[]>('af_activos', { _estado: 'activo', _busq: null }) });

  const filtrados = useMemo(() => activos.filter((a) =>
    (!busq.trim() || a.placa.toLowerCase().includes(busq.toLowerCase()) || a.nombre.toLowerCase().includes(busq.toLowerCase()))
  ), [activos, busq]);
  const seleccionados = filtrados.filter((a) => sel[a.id]);
  const aImprimir = seleccionados.length ? seleccionados : filtrados;

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between gap-3">
        <h1 className="text-xl font-semibold flex items-center gap-2"><QrCode className="w-5 h-5" /> Etiquetas / QR</h1>
        <Button disabled={aImprimir.length === 0} onClick={() => void imprimir(aImprimir.map((a) => ({ placa: a.placa, nombre: a.nombre })))}>
          <Printer className="w-4 h-4 mr-1" /> Imprimir {seleccionados.length ? `(${seleccionados.length})` : `todas (${filtrados.length})`}
        </Button>
      </div>
      <p className="text-sm text-muted-foreground">Genera etiquetas con la placa y su código QR (el QR contiene la placa, que es lo que se escanea en la toma física).</p>
      <div className="flex flex-wrap items-center gap-2">
        <input className={`${fieldCls} w-56`} placeholder="Buscar placa o nombre…" value={busq} onChange={(e) => setBusq(e.target.value)} />
      </div>
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Activos ({filtrados.length})</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground"><th className="py-2 pr-3 w-8"></th><th className="py-2 pr-3">Placa</th><th className="py-2 pr-3">Activo</th></tr></thead>
            <tbody>
              {filtrados.length === 0 && !isFetching && <tr><td colSpan={3} className="py-8 text-center text-muted-foreground">Sin activos</td></tr>}
              {filtrados.map((a) => (
                <tr key={a.id} className="border-b last:border-0 hover:bg-muted/40">
                  <td className="py-2 pr-3"><input type="checkbox" className="h-4 w-4" checked={!!sel[a.id]} onChange={(e) => setSel((s) => ({ ...s, [a.id]: e.target.checked }))} /></td>
                  <td className="py-2 pr-3 font-mono">{a.placa}</td>
                  <td className="py-2 pr-3">{a.nombre}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
